import { useEffect, useMemo, useState } from 'react';
import { useLocation, useOutletContext, useSearchParams } from 'react-router-dom';
import { weekScoreboard, scoreboardWeek, weekStakes, type ScoreboardGame, type WeekStake } from '@ff/core';
import type { SeasonContext } from '../lib/seasonContext.ts';
import { Avatar } from '../components/Avatar.tsx';
import { NoGamesYet } from '../components/EmptyState.tsx';
import { TrashTalk } from '../components/TrashTalk.tsx';
import { Lineups } from '../components/Lineups.tsx';
import { ManagerLink } from '../components/ManagerLink.tsx';
import { AwardStrip } from '../components/AwardStrip.tsx';
import { points, record, relativeTime } from '../lib/format.ts';

/**
 * Home: where the league is right now, week by week.
 *
 * It reads differently depending on when you open it, because the question
 * changes. While games are on it is a live board — who is leading the weekly
 * pot, who is on the survivor bubble. Once the week is called it is a summary
 * of what that week actually did to the money. Same page, same data; only the
 * tense moves.
 *
 * Below the summary sit the games themselves, head to head, with the trash
 * talk for that week — the thing ESPN's app can't do, and the reason this used
 * to be a separate Scoreboard tab.
 *
 * The side pots close it out, the same tickets Standings shows. Home is the
 * page everybody opens, and a pot you have to know to go looking for is a pot
 * nobody follows.
 */
export function Home() {
  const { stored, nameOf } = useOutletContext<SeasonContext>();
  const { data, money } = stored.bundle;

  const weeks = useMemo(
    () => [...new Set(data.matchups.map((m) => m.week))].sort((a, b) => a - b),
    [data],
  );
  const defaultWeek = useMemo(() => scoreboardWeek(data), [data]);

  // The week the user explicitly picked, or null to follow the default. Derive
  // the shown week each render so it (a) advances to the live week as polling
  // updates the data, and (b) never points at a week missing from the current
  // season after a switch.
  // A trash-talk notification links to ?week=N. Followed rather than merely
  // seeded, because tapping a second notification while the app is already open
  // changes the query string without remounting this page.
  const [params] = useSearchParams();
  const linkedWeek = Number(params.get('week'));
  const validLink = Number.isInteger(linkedWeek) && linkedWeek > 0 ? linkedWeek : null;
  const focusPostId = params.get('post');
  // Every navigation gets a new key, so a second notification for a week the
  // URL already names still re-selects it (and re-focuses its post) even after
  // the user has picked a different week by hand.
  const { key: focusKey } = useLocation();
  const [picked, setPicked] = useState<number | null>(validLink);
  useEffect(() => {
    if (validLink !== null) setPicked(validLink);
  }, [validLink, focusKey]);
  const week = picked !== null && weeks.includes(picked) ? picked : defaultWeek;

  if (weeks.length === 0 || week === null) {
    return (
      <div className="space-y-4">
        <Header
          season={data.season}
          week={null}
          weeks={[]}
          status="none"
          onPick={() => {}}
        />
        <NoGamesYet season={data.season} />
        <SidePots />
      </div>
    );
  }

  const games = weekScoreboard(data, money, week);
  const stakes = weekStakes(data, money, week);
  // The week's own state, not the season's: looking back at week 1 in November
  // should read as settled even though another week is live right now.
  const status: WeekStatus = games.every((g) => g.status === 'scheduled')
    ? 'upcoming'
    : games.every((g) => g.status === 'final')
      ? 'final'
      : 'live';

  return (
    <div className="space-y-6">
      <Header
        season={data.season}
        week={week}
        weeks={weeks}
        status={status}
        onPick={setPicked}
      />

      <WeekSummary stakes={stakes} status={status} nameOf={nameOf} />

      <div className="grid items-start gap-3 sm:grid-cols-2">
        {games.map((g, i) => (
          <GameCard
            key={i}
            game={g}
            season={data.season}
            syncedAt={stored.syncedAt}
            nameOf={nameOf}
          />
        ))}
      </div>

      <Freshness syncedAt={stored.syncedAt} live={data.liveWeeks.length > 0} />

      <hr className="border-felt-line" />
      <TrashTalk season={data.season} week={week} focusPostId={focusPostId} focusKey={focusKey} />

      <hr className="border-felt-line" />
      <SidePots />
    </div>
  );
}

/**
 * The season's side pots, shown before a snap too: they exist from the moment
 * the league does, and the Clown's ticket is the way into its draft board.
 */
function SidePots() {
  const { stored, nameOf } = useOutletContext<SeasonContext>();
  const { money } = stored.bundle;
  return (
    <section>
      <h2 className="eyebrow mb-3">The Side Pots</h2>
      <AwardStrip awards={money.awards} config={money.config} nameOf={nameOf} />
    </section>
  );
}

/** How the week the page is showing is going. */
type WeekStatus = 'none' | 'upcoming' | 'live' | 'final';

function Header({
  season,
  week,
  weeks,
  status,
  onPick,
}: {
  season: number;
  week: number | null;
  weeks: number[];
  status: WeekStatus;
  onPick: (w: number) => void;
}) {
  const eyebrow =
    status === 'live'
      ? 'games on now'
      : status === 'final'
        ? `week ${week} · final`
        : status === 'upcoming'
          ? `week ${week} · upcoming`
          : 'head to head';
  return (
    <div>
      <p className="eyebrow">
        {season} · {eyebrow}
      </p>
      <div className="mt-1 flex flex-wrap items-center gap-4">
        <h1 className="font-display text-3xl font-black uppercase tracking-tight">
          This Week
        </h1>
        {weeks.length > 0 && week !== null && (
          <select
            value={week}
            onChange={(e) => onPick(Number(e.target.value))}
            className="rounded-md border border-felt-line bg-felt-raised px-2 py-1 font-mono text-sm text-chalk"
          >
            {weeks.map((w) => (
              <option key={w} value={w}>
                Week {w}
              </option>
            ))}
          </select>
        )}
      </div>
    </div>
  );
}

/**
 * The week in one panel: what is being won right now, or what was won.
 *
 * Every line comes from `weekStakes`, the same computation that hangs the notes
 * on the matchup cards below — so the summary and the cards can never name
 * different managers for the same pot.
 *
 * Nothing to say is a real answer. A week nobody has played yet has no leader,
 * and an empty panel is better than a row of dashes pretending otherwise.
 */
function WeekSummary({
  stakes,
  status,
  nameOf,
}: {
  stakes: WeekStake[];
  status: WeekStatus;
  nameOf: (id: string) => string;
}) {
  if (stakes.length === 0) return null;

  return (
    <section className="card p-4">
      <h2 className="eyebrow">
        {status === 'final' ? 'What this week moved' : "What's on the line"}
      </h2>
      <dl className="mt-3 space-y-2">
        {stakes.map((s) => (
          <div
            key={`${s.kind}:${s.label}`}
            className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1"
          >
            <dt className="flex items-baseline gap-2 font-mono text-xs text-chalk-dim">
              {!s.settled && (
                <span className="h-1.5 w-1.5 shrink-0 animate-pulse rounded-full bg-cash" />
              )}
              {stakeLabel(s)}
            </dt>
            <dd className="flex items-baseline gap-2">
              <span className="font-display font-bold">
                {/* A tie is two names and real money split between them. */}
                {s.ownerIds.map((id) => nameOf(id)).join(' & ')}
              </span>
              {s.detail && (
                <span className="font-mono text-[0.7rem] tabular text-chalk-dim">
                  {s.detail}
                </span>
              )}
            </dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

/**
 * The left-hand side of a summary row: what the pot is, and whether it is
 * decided. A pool this league doesn't pay has no dollar figure to show.
 */
function stakeLabel(s: WeekStake): string {
  const dollars = s.amount === null ? '' : `$${s.amount} `;
  if (s.kind === 'survivor') {
    return s.settled ? `${dollars}${s.label} · out` : `${dollars}${s.label} · bubble`;
  }
  return s.settled ? `${dollars}${s.label} · banked` : `${dollars}${s.label} · leading`;
}

/**
 * How stale the numbers are.
 *
 * The book syncs itself every couple of minutes during games, keeps checking
 * every quarter hour until a week is actually called, and once a day after
 * that — all of which is invisible until somebody refreshes twice and sees the
 * same score. Saying so costs one line and saves the "is this thing broken" text.
 */
function Freshness({ syncedAt, live }: { syncedAt: string; live: boolean }) {
  const ago = relativeTime(syncedAt);
  if (!ago) return null;
  return (
    <p className="font-mono text-[0.65rem] text-chalk-dim">
      ESPN data as of {ago}
      {live
        ? ' · refreshing every couple of minutes while games are on'
        : ' · the book keeps checking until a week is called, then once a day'}
    </p>
  );
}

function GameCard({
  game,
  season,
  syncedAt,
  nameOf,
}: {
  game: ScoreboardGame;
  season: number;
  syncedAt: string;
  nameOf: (id: string) => string;
}) {
  const [open, setOpen] = useState(false);

  return (
    // An open card takes the whole row. Kept at half width it left a column of
    // dead space beside it while truncating the player names inside it — the
    // one place on the page where width is actually worth something.
    <div className={`card flex flex-col gap-2 p-4 ${open ? 'sm:col-span-2' : ''}`}>
      {/* The whole card is the control: on a phone, asking someone to hit a
          small chevron is asking them not to bother.
          
          The button is an overlay behind the summary rather than a wrapper
          around it, because the managers' names in there are links. Nesting an
          <a> inside a <button> is invalid, and a click handler that stops
          propagation only fixes the mouse — keyboard users would still tab into
          a link inside a button, and a screen reader would announce one control
          where there are three. As siblings, each is reachable and announced on
          its own; the summary lets pointer events fall through to the overlay
          except on the links themselves. */}
      <div className="relative">
        <button
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-label={`${open ? 'Hide' : 'Show'} lineups: ${nameOf(game.home.ownerId)} against ${nameOf(game.away.ownerId)}`}
          className="absolute inset-0 rounded-md focus-visible:ring-2 focus-visible:ring-brass"
        />
        <div className="pointer-events-none relative flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <StatusChip status={game.status} />
            <span className="flex items-center gap-2 font-mono text-xs text-chalk-dim">
              {game.status !== 'scheduled' &&
                (game.leaderId ? `by ${points(game.margin)}` : 'tied')}
              <span className="text-brass">{open ? '▾' : '▸'}</span>
            </span>
          </div>

          <Side game={game} which="home" nameOf={nameOf} />
          <Side game={game} which="away" nameOf={nameOf} />
        </div>
      </div>

      {game.stakes.length > 0 && (
        <div className="mt-1 border-t border-felt-line pt-2">
          {game.stakes.map((s) => (
            <p key={s} className="font-mono text-[0.7rem] text-brass">
              ▲ {s}
            </p>
          ))}
        </div>
      )}

      {open && (
        <div className="border-t border-felt-line">
          <Lineups
            season={season}
            week={game.week}
            syncedAt={syncedAt}
            homeOwnerId={game.home.ownerId}
            awayOwnerId={game.away.ownerId}
            nameOf={nameOf}
          />
        </div>
      )}
    </div>
  );
}

function Side({
  game,
  which,
  nameOf,
}: {
  game: ScoreboardGame;
  which: 'home' | 'away';
  nameOf: (id: string) => string;
}) {
  const s = game[which];
  const leading = game.leaderId === s.ownerId;
  const scheduled = game.status === 'scheduled';

  return (
    <div className={`flex items-center gap-3 ${game.leaderId && !leading ? 'opacity-60' : ''}`}>
      <Avatar name={nameOf(s.ownerId)} size={36} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          {/* pointer-events-auto: the summary around this is transparent to
              clicks so they reach the expand overlay; the name is the exception. */}
          <span className="pointer-events-auto truncate font-display font-bold">
            <ManagerLink ownerId={s.ownerId}>{nameOf(s.ownerId)}</ManagerLink>
          </span>
          {leading && <span className="text-brass">◀</span>}
        </div>
        <div className="font-mono text-[0.7rem] text-chalk-dim tabular">
          {record(s.wins, s.losses, s.ties)}
        </div>
      </div>
      <div
        className={`font-mono text-2xl font-bold tabular ${
          scheduled ? 'text-chalk-dim' : leading ? 'text-cash' : 'text-chalk'
        }`}
      >
        {scheduled ? '—' : points(s.score)}
      </div>
    </div>
  );
}

function StatusChip({ status }: { status: ScoreboardGame['status'] }) {
  if (status === 'live') {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-blood/15 px-2 py-0.5 font-mono text-[0.65rem] uppercase tracking-wider text-blood">
        <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-blood" />
        Live
      </span>
    );
  }
  return (
    <span className="font-mono text-[0.65rem] uppercase tracking-wider text-chalk-dim">
      {status === 'final' ? 'Final' : 'Upcoming'}
    </span>
  );
}
